<template>
  <section class="card">
    <header class="head">
      <div>
        <h2>全部用户</h2>
        <p class="import-hint muted">
          用户列表；「单位信息」与小程序「我的单位」一致。关闭详情后自动刷新列表。
        </p>
      </div>
      <div class="head-actions">
        <button class="btn btn-edit" type="button" @click="load">刷新</button>
      </div>
    </header>

    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="filters filters-panel">
      <div class="flt">
        <input
          v-model.trim="keyword"
          class="flt-input flt-input--wide"
          placeholder="姓名 / 手机 / 单位名称 / 单位职务"
          aria-label="姓名、手机、单位名称、单位职务"
          @keyup.enter="onSearch"
        />
      </div>
      <div class="flt">
        <FancySelect
          v-model="filterStatusCode"
          class="flt-select"
          :options="statusFilterOptions"
          placeholder="全部状态"
          @change="onStatusFilterChange"
        />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" @click="onSearch">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th class="col-seq center">序号</th>
          <th class="center">姓名</th>
          <th class="center">手机</th>
          <th class="center">单位名称</th>
          <th class="center">单位身份</th>
          <th class="center">单位职务</th>
          <th class="col-status center">状态</th>
          <th class="center">会员编号</th>
          <th class="center">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in pagedRows" :key="row.id">
          <td class="col-seq center">{{ rowOffset + idx + 1 }}</td>
          <td class="center">{{ row.registerName || '—' }}</td>
          <td class="center">{{ row.registerMobile || '—' }}</td>
          <td class="center ellipsis" :title="row.registerCompanyName || ''">{{ row.registerCompanyName || '—' }}</td>
          <td class="center ellipsis" :title="row.memberLevel || ''">{{ row.memberLevel || '—' }}</td>
          <td class="center ellipsis" :title="row.registerTitle || ''">{{ row.registerTitle || '—' }}</td>
          <td class="col-status center">
            <span class="status-pill" :class="statusPillClass(row.statusLabel)">{{ row.statusLabel || '—' }}</span>
          </td>
          <td class="center">{{ row.formalMemberCode || '—' }}</td>
          <td class="center">
            <button class="btn btn-edit" type="button" @click="openDetail(row)">详细信息</button>
          </td>
        </tr>
      </tbody>
    </table>

    <ListPager
      v-model:page="page"
      v-model:page-size="pageSize"
      :total="sortedRows.length"
    />

    <div v-if="showDetail" class="mask" @click.self="closeDetail">
      <div class="dialog dialog--shell dialog-detail dialog-mp" @click.stop>
        <header class="mp-detail-bar">
          <span class="mp-detail-bar__title">详细信息</span>
          <span class="mp-detail-bar__close" role="button" tabindex="0" @click="closeDetail" @keydown.enter="closeDetail">关闭</span>
        </header>

        <p v-if="detailError" class="error">{{ detailError }}</p>

        <template v-else-if="detail">
          <div class="detail-meta-card">
            <span class="status-pill" :class="statusPillClass(detail.statusLabel)">{{ detail.statusLabel || '—' }}</span>
            <span class="detail-meta-card__sep" aria-hidden="true" />
            <span class="detail-meta-card__text">会员编号 <strong>{{ detail.formalMemberCode || '—' }}</strong></span>
            <span class="detail-meta-card__sep" aria-hidden="true" />
            <span class="detail-meta-card__muted">创建 {{ detail.createdAt || '—' }}</span>
            <span class="detail-meta-card__sep" aria-hidden="true" />
            <span class="detail-meta-card__muted">更新 {{ detail.memberUpdatedAt || '—' }}</span>
          </div>
          <div class="detail-actions">
            <button type="button" class="btn btn-primary" @click="goEditMembershipApply">编辑入会申请表</button>
            <button v-if="latestMembershipApplicationId" type="button" class="btn btn-edit" @click="goMembershipApplyList">
              入会申请记录
            </button>
            <p class="detail-actions-hint muted">
              修改活动报名者填报：会务活动 → 报名名单（按人）→「编辑报名填报」（非本页按钮）。
            </p>
          </div>

          <nav class="detail-subnav" aria-label="详情分区">
            <button type="button" class="detail-subnav__item" :class="{ active: detailFocus === 'person' }" @click="focusDetailPanel('person')">
              个人信息
            </button>
            <button type="button" class="detail-subnav__item" :class="{ active: detailFocus === 'unit' }" @click="focusDetailPanel('unit')">
              单位信息
            </button>
            <button type="button" class="detail-subnav__item" :class="{ active: detailFocus === 'engagement' }" @click="focusDetailPanel('engagement')">
              活跃度
            </button>
          </nav>

          <div class="detail-split">
            <!-- 个人信息：仅在选择「个人信息」时展示 -->
            <section v-show="detailFocus === 'person'" id="archive-person" class="mp-sheet mp-sheet--single">
              <div class="mp-sheet__head">
                <span class="mp-sheet__title">个人资料</span>
              </div>
              <div class="mp-profile-card mp-profile-card--avatar-only">
                <div class="mp-profile-card__avatar-wrap">
                  <img
                    v-if="avatarImgSrc"
                    class="mp-profile-card__avatar"
                    :src="avatarImgSrc"
                    alt=""
                    @error="avatarLoadErr = true"
                  />
                  <div v-else class="mp-profile-card__avatar mp-profile-card__avatar--ph" />
                </div>
              </div>

              <div class="mp-form-block">
                <div class="mp-row">
                  <span class="mp-row__lab">姓名</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.registerName) }}</span>
                </div>
                <div class="mp-row">
                  <span class="mp-row__lab">手机</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.registerMobile) }}</span>
                </div>
                <div class="mp-row">
                  <span class="mp-row__lab">单位名称</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.registerCompanyName) }}</span>
                </div>
                <div class="mp-row">
                  <span class="mp-row__lab">单位职务</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.registerTitle) }}</span>
                </div>
                <div class="mp-row">
                  <span class="mp-row__lab"><span class="mp-req">*</span>性别</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.personalGender) }}</span>
                </div>
                <div class="mp-row">
                  <span class="mp-row__lab">邮箱</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.personalEmail) }}</span>
                </div>
                <div class="mp-row">
                  <span class="mp-row__lab">微信号</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.personalWechat) }}</span>
                </div>
                <div class="mp-row">
                  <span class="mp-row__lab">籍贯</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.personalNativePlace) }}</span>
                </div>
                <div class="mp-row">
                  <span class="mp-row__lab">毕业院校</span>
                  <span class="mp-row__val mp-row__val--muted">{{ archiveStr(detail.personalSchool) }}</span>
                </div>
                <div class="mp-row mp-row--block">
                  <span class="mp-row__lab">可提供资源</span>
                  <div
                    class="mp-row__val mp-row__val--muted"
                    :class="{ 'mp-row__val--multiline': !isArchiveFieldEmpty(detail.personalSupplyResource) }"
                  >{{ archiveStr(detail.personalSupplyResource) }}</div>
                </div>
                <div class="mp-row mp-row--block">
                  <span class="mp-row__lab">需求资源</span>
                  <div
                    class="mp-row__val mp-row__val--muted"
                    :class="{ 'mp-row__val--multiline': !isArchiveFieldEmpty(detail.personalNeedResource) }"
                  >{{ archiveStr(detail.personalNeedResource) }}</div>
                </div>
              </div>
            </section>

            <!-- 单位信息：与小程序「我的单位」一致 — 手填 + 已通过入会申请摘要 -->
            <section v-show="detailFocus === 'unit'" id="archive-unit" class="mp-sheet mp-sheet--wide">
              <div class="mp-sheet__head">
                <span class="mp-sheet__title">单位信息</span>
              </div>

              <table v-if="unitArchiveRows.length" class="archive-unit-table">
                <thead>
                  <tr>
                    <th class="col-seq">序号</th>
                    <th>单位名称</th>
                    <th>职务</th>
                    <th>地址</th>
                    <th>行业</th>
                    <th>审核时间</th>
                    <th>创建时间</th>
                    <th>更新时间</th>
                    <th>默认展示</th>
                    <th class="col-act">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="r in unitArchiveRows"
                    :key="r.key"
                    class="archive-unit-tr"
                    :class="{ 'archive-unit-tr--apply': r.kind === 'approved', 'archive-unit-tr--manual': r.kind === 'manual' }"
                  >
                    <td class="col-seq">{{ r.seq }}</td>
                    <td>{{ archiveStr(r.companyName) }}</td>
                    <td>{{ archiveStr(r.duty) }}</td>
                    <td class="td-wrap">{{ archiveStr(r.address) }}</td>
                    <td>{{ archiveStr(r.industry) }}</td>
                    <td class="td-muted">{{ archiveStr(r.reviewedAt) }}</td>
                    <td class="td-muted">{{ archiveStr(r.createdAt) }}</td>
                    <td class="td-muted">{{ archiveStr(r.updatedAt) }}</td>
                    <td>{{ archiveStr(r.profileDefault) }}</td>
                    <td class="col-act td-act">
                      <template v-if="r.kind === 'manual'">
                        <button type="button" class="btn btn-edit btn--compact" @click="openManualUnitDetail(r.rawManual)">
                          查看完整资料
                        </button>
                      </template>
                      <template v-else>
                        <span class="muted">—</span>
                      </template>
                      <span v-if="r.membershipJoined" class="archive-apply-corner">已入会</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="mp-hint mp-hint--tight">暂无单位信息</p>
            </section>

            <section v-show="detailFocus === 'engagement'" id="archive-engagement" class="mp-sheet mp-sheet--wide">
              <div class="mp-sheet__head">
                <span class="mp-sheet__title">活跃度（近 90 天）</span>
              </div>
              <MemberEngagementPanel v-if="detail && detail.id" :member-id="detail.id" />
            </section>
          </div>
        </template>
        <div v-else class="detail-loading">加载中…</div>
      </div>
    </div>

    <!-- 手填单位：结构化详情（点击「查看完整资料」） -->
    <div v-if="manualUnitDetail" class="mask mask--nested" @click.self="closeManualUnitDetail">
      <div class="dialog dialog-manual-unit" @click.stop>
        <header class="manual-unit-head">
          <span class="manual-unit-head__title">单位完整资料 · {{ archiveStr(manualUnitDetail.unitName) }}</span>
          <button type="button" class="manual-unit-head__close" @click="closeManualUnitDetail">关闭</button>
        </header>
        <div class="manual-unit-body">
          <div class="mp-row mp-row--readonly mp-row--dense">
            <span class="mp-row__lab">默认展示</span>
            <span class="mp-row__val">{{ manualUnitDetail.profileDefault ? '是' : '否' }}</span>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense">
            <span class="mp-row__lab">单位职务</span>
            <span class="mp-row__val">{{ archiveStr(manualExtraField(manualUnitDetail, 'position')) }}</span>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense mp-row--block">
            <span class="mp-row__lab">单位地址</span>
            <span
              class="mp-row__val"
              :class="{ 'mp-row__val--multiline': !isArchiveFieldEmpty(manualExtraField(manualUnitDetail, 'address')) }"
            >{{ archiveStr(manualExtraField(manualUnitDetail, 'address')) }}</span>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense mp-row--block">
            <span class="mp-row__lab">简介</span>
            <span
              class="mp-row__val"
              :class="{ 'mp-row__val--multiline': !isArchiveFieldEmpty(manualExtraField(manualUnitDetail, 'intro')) }"
            >{{ archiveStr(manualExtraField(manualUnitDetail, 'intro')) }}</span>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense mp-row--block">
            <span class="mp-row__lab">主营业务</span>
            <div class="tag-block">{{ manualExtraArrayText(manualUnitDetail, 'bizList') }}</div>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense mp-row--block">
            <span class="mp-row__lab">社会荣誉/职务</span>
            <div class="tag-block">{{ manualExtraArrayText(manualUnitDetail, 'honorList') }}</div>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense mp-row--block">
            <span class="mp-row__lab">单位 Logo</span>
            <div v-if="manualLogoSrc(manualUnitDetail)" class="manual-logo-wrap">
              <img
                class="manual-logo-img"
                :src="manualLogoSrc(manualUnitDetail)"
                alt=""
                @error="onManualLogoError"
              />
            </div>
            <span v-else class="mp-row__val mp-row__val--muted">—</span>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense mp-row--block">
            <span class="mp-row__lab">风采相册</span>
            <div v-if="manualAlbumUrls(manualUnitDetail).length" class="manual-album-grid">
              <img
                v-for="(src, i) in manualAlbumUrls(manualUnitDetail)"
                :key="'al-' + i"
                class="manual-album-thumb"
                :src="src"
                alt=""
              />
            </div>
            <span v-else class="mp-row__val mp-row__val--muted">—</span>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense mp-row--block">
            <span class="mp-row__lab">宣传视频</span>
            <div v-if="manualPromoVideoSrc(manualUnitDetail)" class="manual-media-block">
              <video
                class="manual-video"
                controls
                playsinline
                preload="metadata"
                :src="manualPromoVideoSrc(manualUnitDetail)"
              />
              <div class="manual-media-caption muted">{{ archiveStr(manualExtraField(manualUnitDetail, 'promoVideoName')) }}</div>
            </div>
            <span v-else class="mp-row__val mp-row__val--muted">—</span>
          </div>
          <div class="mp-row mp-row--readonly mp-row--dense mp-row--block">
            <span class="mp-row__lab">电子文档</span>
            <div v-if="manualDocHref(manualUnitDetail)" class="manual-media-block">
              <a class="manual-doc-link" :href="manualDocHref(manualUnitDetail)" target="_blank" rel="noopener noreferrer">
                打开文档：{{ archiveStr(manualExtraField(manualUnitDetail, 'docName')) }}
              </a>
            </div>
            <span v-else class="mp-row__val mp-row__val--muted">—</span>
          </div>
          <details class="archive-collapsible archive-collapsible--mono archive-collapsible--mt">
            <summary>原始 JSON（技术排查）</summary>
            <pre class="manual-json-pre">{{ manualUnitDetailJson }}</pre>
          </details>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  BASE_URL,
  getAdminMemberMapProfiles,
  getAdminMemberMapProfileDetail,
} from '@/api'
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import MemberEngagementPanel from '@/components/member/MemberEngagementPanel.vue'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'

/** 同步 URL 筛选时避免 $route.query 监听重复触发 */
let skipRouteQueryWatch = false

export default {
  name: 'MemberMapProfileManageView',
  components: { FancySelect, ListPager, MemberEngagementPanel },
  data() {
    return {
      list: [],
      keyword: '',
      filterStatusCode: '',
      page: 1,
      pageSize: 10,
      errorMsg: '',
      showDetail: false,
      detail: null,
      detailError: '',
      /** 详情弹层：子导航高亮、头像加载失败时占位 */
      detailFocus: 'person',
      avatarLoadErr: false,
      /** 手填单位详情浮层 */
      manualUnitDetail: null,
      manualLogoBroken: false,
    }
  },
  computed: {
    latestMembershipApplicationId() {
      const list = this.approvedMembershipUnits || []
      if (!list.length) return null
      const sorted = [...list].sort((a, b) => Number(b.applicationId || 0) - Number(a.applicationId || 0))
      const id = sorted[0] && sorted[0].applicationId
      return id != null && Number(id) > 0 ? Number(id) : null
    },
    manualUnitDetailJson() {
      const u = this.manualUnitDetail
      if (!u) return ''
      const ex = u.extra && typeof u.extra === 'object' ? u.extra : {}
      try {
        return JSON.stringify(ex, null, 2)
      } catch {
        return String(ex)
      }
    },
    avatarImgSrc() {
      if (!this.detail || this.avatarLoadErr) return ''
      const u = String(this.detail.avatarUrl || '').trim()
      if (!u) return ''
      return u
    },
    /** 已通过入会申请单位（按申请 id 升序） */
    approvedMembershipUnits() {
      const d = this.detail
      if (!d || !Array.isArray(d.approvedMembershipUnits)) return []
      return d.approvedMembershipUnits
    },
    /** 小程序「我的单位」手填行（合并入已通过申请前可见） */
    manualMembershipUnits() {
      const d = this.detail
      if (!d || !Array.isArray(d.manualMembershipUnits)) return []
      return d.manualMembershipUnits
    },
    /** 单位归档：手填单位为主；与入会申请单位名一致时合并审核时间/已入会到同一行 */
    unitArchiveRows() {
      const d = this.detail
      if (!d) return []
      const rows = []
      let seq = 1
      const appr = (this.approvedMembershipUnits || [])
        .slice()
        .sort((a, b) => Number(a.applicationId || 0) - Number(b.applicationId || 0))
      const usedApprIds = new Set()
      const man = (this.manualMembershipUnits || [])
        .slice()
        .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
      for (const u of man) {
        const match = this.findApprovedUnitMatch(u.unitName, appr, usedApprIds)
        if (match && match.applicationId != null) {
          usedApprIds.add(match.applicationId)
        }
        const addrManual = this.manualExtraField(u, 'address')
        rows.push({
          key: `m-${u.id}`,
          kind: 'manual',
          seq: seq++,
          companyName: u.unitName,
          duty: match && match.repDuty ? match.repDuty : this.manualExtraField(u, 'position'),
          address: addrManual && addrManual !== '—' ? addrManual : match ? match.unitAddress || '—' : '—',
          industry: match && match.industry ? match.industry : '—',
          reviewedAt: match && match.reviewedAt ? match.reviewedAt : '—',
          createdAt: u.createdAt || u.created_at || '—',
          updatedAt: u.updatedAt || u.updated_at || '—',
          profileDefault: u.profileDefault ? '是' : '否',
          membershipJoined: !!match,
          rawManual: u,
        })
      }
      for (const u of appr) {
        if (u.applicationId != null && usedApprIds.has(u.applicationId)) {
          continue
        }
        rows.push({
          key: `a-${u.applicationId}`,
          kind: 'approved',
          seq: seq++,
          companyName: u.companyName,
          duty: u.repDuty,
          address: u.unitAddress,
          industry: u.industry,
          reviewedAt: u.reviewedAt || '—',
          createdAt: '—',
          updatedAt: '—',
          profileDefault: '—',
          membershipJoined: true,
          rawManual: null,
        })
      }
      return rows
    },
    statusFilterOptions() {
      return [
        { value: '', label: '全部状态' },
        { value: 'REGISTERED', label: '普通用户' },
        { value: 'MEMBERSHIP_REVIEWING', label: '入会审核中' },
        { value: 'ACTIVE', label: '正式会员' },
      ]
    },
    sortedRows() {
      let arr = (this.list || []).slice().sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
      const fs = String(this.filterStatusCode || '').trim().toUpperCase()
      if (fs) {
        arr = arr.filter((r) => String(r.statusCode || '').trim().toUpperCase() === fs)
      }
      const q = String(this.keyword || '').trim().toLowerCase()
      if (!q) return arr
      return arr.filter((r) => {
        const hay = [r.registerName, r.registerMobile, r.registerCompanyName, r.memberLevel, r.registerTitle, r.statusLabel, r.statusCode, r.formalMemberCode]
          .map((x) => String(x == null ? '' : x).toLowerCase())
          .join(' ')
        return hay.indexOf(q) >= 0
      })
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    pagedRows() {
      return slicePageRows(this.sortedRows, this.page, this.pageSize)
    },
  },
  watch: {
    'sortedRows.length'(n) {
      this.page = clampPage(this.page, n, this.pageSize)
    },
    detail(val) {
      if (val) this.avatarLoadErr = false
    },
    '$route.query': {
      deep: true,
      async handler() {
        if (skipRouteQueryWatch) return
        this.applyFiltersFromRoute()
        await this.tryOpenFocusFromRoute()
      },
    },
  },
  async mounted() {
    this.applyFiltersFromRoute()
    await this.load()
    await this.tryOpenFocusFromRoute()
  },
  activated() {
    this.applyFiltersFromRoute()
  },
  methods: {
    applyFiltersFromRoute(route = this.$route) {
      const q = route?.query || {}
      this.filterStatusCode = q.status != null ? String(q.status) : ''
    },
    buildFilterQuery() {
      const q = { ...(this.$route?.query || {}) }
      const st = String(this.filterStatusCode || '').trim()
      if (st) q.status = st
      else delete q.status
      return q
    },
    async pushFiltersToRoute() {
      const query = this.buildFilterQuery()
      const resolved = this.$router.resolve({ path: this.$route.path, query })
      if (resolved.fullPath === this.$route.fullPath) return
      skipRouteQueryWatch = true
      try {
        await this.$router.replace({ path: this.$route.path, query })
      } catch {
        /* ignore */
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    async onStatusFilterChange() {
      this.page = 1
      skipRouteQueryWatch = true
      try {
        await this.pushFiltersToRoute()
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    async onSearch() {
      this.page = 1
      await this.onStatusFilterChange()
    },
    async resetFilters() {
      this.page = 1
      this.keyword = ''
      this.filterStatusCode = ''
      await this.pushFiltersToRoute()
    },
    unitNameMatchKey(raw) {
      if (raw == null || raw === undefined) return ''
      const s = String(raw).trim().replace(/\u3000/g, ' ')
      let out = ''
      for (let i = 0; i < s.length; i++) {
        const c = s.charAt(i)
        if (/\s/.test(c)) continue
        out += c.toLowerCase()
      }
      return out
    },
    findApprovedUnitMatch(unitName, approvedList, usedIds) {
      const key = this.unitNameMatchKey(unitName)
      if (!key) return null
      for (const a of approvedList || []) {
        if (!a) continue
        const aid = a.applicationId
        if (aid != null && usedIds && usedIds.has(aid)) continue
        if (this.unitNameMatchKey(a.companyName) === key) {
          return a
        }
      }
      return null
    },
    archiveStr(v) {
      const s = v == null ? '' : String(v).trim()
      return s || '—'
    },
    /** 有实质内容时才用多行块样式，避免「—」占大块灰底 */
    isArchiveFieldEmpty(v) {
      return v == null || String(v).trim() === ''
    },
    statusPillClass(label) {
      const t = String(label || '')
      if (t.includes('正式')) return 'status-pill--active'
      if (t.includes('入会') || t.includes('审核')) return 'status-pill--review'
      return 'status-pill--idle'
    },
    async load() {
      this.errorMsg = ''
      try {
        this.list = (await getAdminMemberMapProfiles()) || []
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
        this.list = []
      }
    },
    async tryOpenFocusFromRoute() {
      const raw = this.$route?.query?.focusMemberId
      const id = Number(raw)
      if (!Number.isFinite(id) || id <= 0) return
      const row = (this.list || []).find((r) => Number(r.id) === id)
      if (row) {
        await this.openDetail(row)
      }
    },
    focusDetailPanel(which) {
      if (which === 'unit') this.detailFocus = 'unit'
      else if (which === 'engagement') this.detailFocus = 'engagement'
      else this.detailFocus = 'person'
    },
    async openDetail(row) {
      if (!row || row.id == null) return
      this.manualUnitDetail = null
      this.showDetail = true
      this.detail = null
      this.detailError = ''
      this.detailFocus = 'person'
      this.avatarLoadErr = false
      try {
        this.detail = await getAdminMemberMapProfileDetail(row.id)
      } catch (e) {
        this.detailError = e.message || '加载详情失败'
      }
    },
    closeDetail() {
      this.showDetail = false
      this.detail = null
      this.detailError = ''
      this.manualUnitDetail = null
      this.load()
    },
    closeManualUnitDetail() {
      this.manualUnitDetail = null
    },
    openManualUnitDetail(row) {
      this.manualLogoBroken = false
      this.manualUnitDetail = row && typeof row === 'object' ? { ...row } : null
    },
    onManualLogoError() {
      this.manualLogoBroken = true
    },
    goEditMembershipApply() {
      const id = this.latestMembershipApplicationId
      if (!id) {
        window.alert('暂无已通过入会申请，无法编辑填报内容')
        return
      }
      this.$router.push({
        path: '/form/submissions',
        query: {
          formCode: 'membership_apply',
          openSubmissionId: String(id),
          openEdit: '1',
          returnTo: '/member/map-profiles',
        },
      })
    },
    goMembershipApplyList() {
      const d = this.detail
      if (!d) return
      this.$router.push({
        path: '/form/submissions',
        query: {
          formCode: 'membership_apply',
          applicantName: d.registerName || '',
          applicantUnit: d.registerCompanyName || '',
          returnTo: '/member/map-profiles',
        },
      })
    },
    manualExtra(row) {
      if (!row || !row.extra || typeof row.extra !== 'object') return {}
      return row.extra
    },
    manualExtraField(row, key) {
      const ex = this.manualExtra(row)
      const v = ex[key]
      if (v == null) return ''
      if (typeof v === 'string') return v
      return String(v)
    },
    manualExtraArrayText(row, key) {
      const ex = this.manualExtra(row)
      const raw = ex[key]
      if (!Array.isArray(raw) || !raw.length) return '—'
      return raw.map((x) => String(x == null ? '' : x).trim()).filter(Boolean).join('、') || '—'
    },
    /** 与小程序 {@link mediaUrl.resolveMediaUrl} 对齐：上传落盘多为 {@code images/} / {@code files/}，须拼成 {@code /uploads/...} */
    archiveResolveMedia(path) {
      let u = String(path || '').trim().replace(/\\/g, '/')
      if (!u) return ''
      if (/^(blob:|data:|wxfile:)/i.test(u)) return u
      const base = typeof BASE_URL === 'string' ? BASE_URL.replace(/\/$/, '') : ''

      if (/^https?:\/\//i.test(u)) {
        try {
          const parsed = new URL(u)
          const pth = (parsed.pathname || '').replace(/\\/g, '/')
          const host = parsed.hostname || ''
          const isLanOrLocal =
            host === '127.0.0.1' ||
            host === 'localhost' ||
            /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
            /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host) ||
            /^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(host)
          // 仅开发机/局域网存下来的绝对地址改走当前管理端代理；正式环境外链、CDN 保持原 URL
          if (isLanOrLocal && pth.startsWith('/')) {
            return `${base}${pth}${parsed.search || ''}${parsed.hash || ''}`
          }
        } catch {
          /* fallthrough */
        }
        return u
      }

      // 仅存 images/ files/ 或漏写 uploads 的 /images/
      if (u.startsWith('/images/')) {
        u = `/uploads${u}`
      } else if (/^images\//i.test(u)) {
        u = `/uploads/${u}`
      } else if (/^files\//i.test(u)) {
        u = `/uploads/${u}`
      }

      if (/^(images\/|files\/)/i.test(u) && !/^\/uploads\//i.test(u) && !/^uploads\//i.test(u)) {
        u = `/uploads/${u.replace(/^\//, '')}`
      }
      if (/^uploads\//i.test(u)) {
        u = `/${u}`
      }

      if (u.startsWith('/')) return `${base}${u}`
      return `${base}/${u}`
    },
    manualLogoSrc(row) {
      if (this.manualLogoBroken) return ''
      const ex = this.manualExtra(row)
      const candidates = [
        ex.logoUrl,
        ex.logo_url,
        ex.logo,
        ex.companyLogoUrl,
        ex.company_logo_url,
        this.manualExtraField(row, 'logoUrl'),
      ]
      for (const c of candidates) {
        const url = this.archiveResolveMedia(c)
        if (url) return url
      }
      return ''
    },
    manualAlbumUrls(row) {
      const ex = this.manualExtra(row)
      let arr = ex.albumImages
      if (!Array.isArray(arr) && Array.isArray(ex.album_images)) arr = ex.album_images
      if (!Array.isArray(arr)) return []
      return arr
        .map((item) => {
          if (item == null) return ''
          const s =
            typeof item === 'string'
              ? item.trim()
              : String((item && (item.url || item.path || item.src)) || '').trim()
          return s ? this.archiveResolveMedia(s) : ''
        })
        .filter(Boolean)
    },
    manualPromoVideoSrc(row) {
      const ex = this.manualExtra(row)
      const raw =
        this.manualExtraField(row, 'promoVideoPath') ||
        (ex.promoVideoUrl != null ? String(ex.promoVideoUrl) : '') ||
        (ex.videoUrl != null ? String(ex.videoUrl) : '')
      return this.archiveResolveMedia(raw)
    },
    manualDocHref(row) {
      const ex = this.manualExtra(row)
      const raw =
        this.manualExtraField(row, 'docPath') ||
        (ex.docUrl != null ? String(ex.docUrl) : '') ||
        (ex.documentUrl != null ? String(ex.documentUrl) : '')
      return this.archiveResolveMedia(raw)
    },
  },
}
</script>

<style scoped>
.card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
}
.head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.head h2 {
  margin: 0 0 8px;
}
.head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}
.import-hint {
  font-size: 12px;
  line-height: 1.5;
  margin: 0 0 10px;
}
.muted {
  color: #667085;
}
.filters .flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  color: #1d2939;
  background: #fff;
}
.filters .flt-input--wide {
  width: 280px;
}
.filters .flt-select {
  width: 170px;
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  border-bottom: 1px solid #edf0f5;
  padding: 8px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
}
.center { text-align: center; }
.ellipsis {
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
}
.btn + .btn {
  margin-left: 8px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.error {
  color: #d92d20;
  margin: 0 0 10px;
}
.dialog-mp > .error {
  margin-left: 16px;
  margin-right: 16px;
}
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
.status-pill--active {
  background: #e8fbef;
  color: #067647;
}
.status-pill--review {
  background: #fff7e6;
  color: #b54708;
}
.status-pill--idle {
  background: #f2f4f7;
  color: #475467;
}
.dialog {
  max-width: 96vw;
  background: #fff;
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.12), 0 0 1px rgba(15, 23, 42, 0.08);
}
.dialog-mp {
  width: min(1040px, 96vw);
}
.dialog-detail {
  max-height: 88vh;
  overflow-y: auto;
  padding: 0;
  scrollbar-gutter: stable;
}

/* 仿小程序顶栏 */
.mp-detail-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 4;
  padding: 16px 52px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}
.mp-detail-bar__title {
  font-size: 17px;
  font-weight: 600;
  color: #101828;
  letter-spacing: 0.02em;
}
.mp-detail-bar__close {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #475467;
  cursor: pointer;
  user-select: none;
  padding: 6px 8px;
  border-radius: 8px;
}
.mp-detail-bar__close:hover {
  color: #2f6af6;
  background: rgba(47, 106, 246, 0.06);
}

.detail-meta-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
  margin: 16px 20px 14px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f2f4f7 100%);
  border: 1px solid #eaecf0;
  border-radius: 12px;
  font-size: 13px;
  color: #344054;
}
.detail-meta-card__sep {
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #d0d5dd;
  flex-shrink: 0;
}
.detail-meta-card__text strong {
  font-weight: 600;
  color: #101828;
}
.detail-meta-card__muted {
  color: #667085;
  font-size: 12px;
}

.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 20px 14px;
  padding: 0 4px;
  align-items: center;
}
.detail-actions-hint {
  flex: 1 1 100%;
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.5;
}

/* 分段导航（浅色底 + 选中浮起，非实心按钮） */
.detail-subnav {
  display: inline-flex;
  margin: 0 20px 18px;
  padding: 4px;
  background: #eef2f6;
  border-radius: 12px;
  gap: 2px;
}
.detail-subnav__item {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
  padding: 9px 20px;
  margin: 0;
  font-size: 14px;
  color: #667085;
  cursor: pointer;
  border-radius: 9px;
  font-family: inherit;
  transition:
    color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;
}
.detail-subnav__item:hover {
  color: #344054;
}
.detail-subnav__item.active {
  color: #101828;
  font-weight: 600;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

/* 导航切换：同一时间只展示一块内容 */
.detail-split {
  display: block;
  background: #f5f6f8;
  padding: 8px 20px 24px;
  min-height: 200px;
}

.mp-sheet--single {
  max-width: 560px;
  margin: 0 auto;
}
.mp-sheet--wide {
  max-width: 100%;
  margin: 0;
}

.archive-unit-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.archive-unit-table th,
.archive-unit-table td {
  border: 1px solid #eaecf0;
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
}
.archive-unit-table thead th {
  background: #f9fafb;
  font-weight: 600;
  color: #344054;
}
.archive-unit-table .col-seq {
  width: 48px;
  text-align: center;
}
.archive-unit-table .col-act {
  min-width: 120px;
}
.archive-unit-tr--apply {
  background: #fafbff;
}
.archive-unit-tr--manual td {
  color: #1f2937;
}
.td-wrap {
  max-width: 200px;
  word-break: break-word;
}
.td-muted {
  color: #98a2b3;
  font-size: 12px;
}
.td-act {
  position: relative;
  min-height: 40px;
  padding-bottom: 22px !important;
}
.archive-apply-corner {
  position: absolute;
  right: 8px;
  bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #175cd3;
  background: #eff8ff;
  padding: 2px 8px;
  border-radius: 6px;
}
.btn--compact {
  padding: 4px 10px;
  font-size: 12px;
}

.mp-sync-banner {
  font-size: 12px;
  line-height: 1.6;
  color: #475467;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  padding: 10px 12px;
  margin: 0 0 14px;
}
.mp-sync-banner strong {
  color: #1e40af;
}
.mp-sync-banner--unit {
  background: #fffbeb;
  border-color: #fde68a;
}
.mp-sync-banner--unit strong {
  color: #92400e;
}

.mp-sec-title--mt {
  margin-top: 18px;
}
.mp-row--dense {
  padding-top: 6px;
  padding-bottom: 6px;
}
.mp-unit-card {
  border: 1px solid #eaecf0;
  border-radius: 12px;
  padding: 12px 12px 8px;
  margin-bottom: 12px;
  background: #fafbfc;
}
.mp-unit-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.mp-unit-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
}
.mp-unit-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}
.mp-unit-card__badge {
  flex-shrink: 0;
  font-size: 11px;
  color: #027a48;
  background: #ecfdf3;
  border-radius: 6px;
  padding: 2px 8px;
}
.mp-unit-card__badge--def {
  color: #175cd3;
  background: #eff8ff;
}
.mp-unit-card__actions {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #eaecf0;
}
.btn-link-primary {
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: #2f6af6;
  cursor: pointer;
}
.btn-link-primary:hover {
  text-decoration: underline;
}

.archive-collapsible {
  font-size: 12px;
  line-height: 1.5;
  color: #475467;
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 8px 12px;
  margin: 0 0 14px;
}
.archive-collapsible--amber {
  background: #fffbeb;
  border-color: #fde68a;
}
.archive-collapsible--subtle {
  background: #fcfcfd;
  border-style: dashed;
}
.archive-collapsible--mono summary {
  font-family: ui-monospace, monospace;
  font-size: 11px;
}
.archive-collapsible--mt {
  margin-top: 12px;
}
.archive-collapsible summary {
  cursor: pointer;
  font-weight: 600;
  color: #344054;
  user-select: none;
}
.archive-collapsible__body {
  margin: 8px 0 0;
  padding-top: 8px;
  border-top: 1px solid #eaecf0;
}

.mask--nested {
  z-index: 80;
  align-items: flex-start;
  padding-top: 8vh;
}
.dialog-manual-unit {
  width: min(520px, 94vw);
  max-height: 82vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
}
.manual-unit-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #eaecf0;
  background: #fafbfc;
}
.manual-unit-head__title {
  font-size: 15px;
  font-weight: 600;
  color: #101828;
  word-break: break-word;
}
.manual-unit-head__close {
  flex-shrink: 0;
  border: 1px solid #d0d7e2;
  background: #fff;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #344054;
}
.manual-unit-body {
  padding: 12px 16px 20px;
  overflow-y: auto;
  max-height: calc(82vh - 56px);
}
.tag-block {
  font-size: 13px;
  line-height: 1.5;
  color: #1f2a36;
  word-break: break-word;
}
.manual-logo-wrap {
  margin-top: 6px;
}
.manual-logo-img {
  width: 96px;
  height: 96px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid #eaecf0;
  background: #f9fafb;
}
.manual-album-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}
.manual-album-thumb {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #eaecf0;
  background: #f9fafb;
}
.manual-media-block {
  margin-top: 6px;
  max-width: 100%;
}
.manual-video {
  width: 100%;
  max-width: 420px;
  max-height: 240px;
  border-radius: 10px;
  background: #000;
}
.manual-media-caption {
  margin-top: 6px;
  font-size: 12px;
}
.manual-doc-link {
  font-size: 13px;
  color: #175cd3;
  word-break: break-all;
}
.manual-doc-link:hover {
  text-decoration: underline;
}
.manual-json-pre {
  margin: 8px 0 0;
  padding: 10px;
  font-size: 11px;
  line-height: 1.45;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  overflow-x: auto;
  max-height: 220px;
}

.mp-sheet {
  background: #fff;
  border-radius: 16px;
  padding: 18px 16px 20px;
  border: 1px solid #eaecf0;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.04);
  min-width: 0;
}
.mp-sheet__head {
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f2f4f7;
}
.mp-sheet__title {
  font-size: 15px;
  font-weight: 600;
  color: #101828;
}

/* 个人资料卡片（仿小程序 profile 页） */
.mp-profile-card {
  text-align: center;
  padding: 12px 10px 18px;
  margin-bottom: 14px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 55%);
  border-radius: 14px;
  border: 1px solid #f2f4f7;
}
.mp-profile-card__avatar-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
}
.mp-profile-card__avatar {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  object-fit: cover;
  background: #edf0f4;
  box-shadow:
    0 0 0 3px #fff,
    0 4px 14px rgba(15, 23, 42, 0.08);
}
.mp-profile-card__avatar--ph {
  background: linear-gradient(145deg, #e8ecf0, #cfd8dc);
}
.mp-profile-card--avatar-only {
  padding: 16px 10px 14px;
  margin-bottom: 12px;
}
.mp-profile-card--avatar-only .mp-profile-card__avatar-wrap {
  margin-bottom: 0;
}

.mp-form-block {
  border-top: 1px solid #edf0f5;
  padding-top: 4px;
}
.mp-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 6px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 14px;
}
.mp-row:last-child {
  border-bottom: none;
}
.mp-row--block {
  flex-direction: column;
  gap: 6px;
}
.mp-row--readonly .mp-row__lab {
  flex: 0 0 88px;
  color: #667085;
}
.mp-row__lab {
  flex: 0 0 88px;
  color: #667085;
}
.mp-row__val {
  flex: 1;
  color: #1f2a36;
  word-break: break-word;
}
/** 纵向块（标签在上）：勿让内容区 flex:1 吸满剩余高度，否则会出现标签与「—」之间巨大空白 */
.mp-row--block .mp-row__val,
.mp-row--block .tag-block,
.mp-row--block .manual-album-grid,
.mp-row--block .manual-media-block,
.mp-row--block .manual-logo-wrap {
  flex: 0 0 auto !important;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.mp-row--block .mp-row__lab {
  flex: 0 0 auto !important;
  width: 100%;
  max-width: 100%;
}
.mp-row__val--muted {
  color: #98a2b3;
  font-size: 13px;
}
.mp-row__val--multiline {
  display: block;
  padding: 8px 10px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  width: 100%;
  box-sizing: border-box;
}
.mp-req {
  color: #d92d20;
  margin-right: 2px;
}
.mp-sec-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2a36;
  margin-bottom: 12px;
}
.mp-sec-title--sm {
  font-size: 13px;
  margin-top: 14px;
  margin-bottom: 8px;
}
.mp-row--pick .mp-row__val {
  color: #344054;
}
.mp-logo-block {
  margin: 12px 0;
}
.mp-logo-block__head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}
.mp-logo-tip {
  font-size: 11px;
  color: #98a2b3;
}
.mp-logo-ph {
  width: 96px;
  height: 96px;
  border-radius: 8px;
  border: 1px dashed #d0d5dd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #98a2b3;
  background: #fafafa;
}
.mp-more-line {
  height: 1px;
  background: #edf0f5;
  margin: 14px 0 10px;
}
.mp-more-label {
  font-size: 13px;
  color: #667085;
  text-align: center;
  margin-bottom: 8px;
}
.mp-hint {
  font-size: 12px;
  line-height: 1.55;
  color: #667085;
  margin: 10px 0 0;
}
.mp-hint--tight {
  margin: 0 0 8px;
}

.mp-map-block {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #edf0f5;
}
.mp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}
.mp-field__lab {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
}
.mp-field__inp {
  height: 38px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 13px;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.mp-field__inp:focus {
  outline: none;
  border-color: #84adf8;
  box-shadow: 0 0 0 3px rgba(47, 106, 246, 0.12);
}
.mp-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.mp-field--half {
  margin-bottom: 10px;
}

.text-link {
  display: inline-block;
  margin-top: 8px;
  font-size: 13px;
  color: #2f6af6;
  cursor: pointer;
  user-select: none;
}
.text-link:hover {
  text-decoration: underline;
}
.text-link--save {
  margin-top: 12px;
  font-weight: 600;
}
.detail-loading {
  padding: 32px 24px 40px;
  text-align: center;
  font-size: 14px;
  color: #667085;
}
</style>
